import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Fab,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  CheckCircle as CompleteIcon,
  Assignment as AssignmentIcon,
  PendingActions as PendingActionsIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
} from '@mui/icons-material';
import {
  useVetInstructions,
  useCreateVetInstruction,
  useCompleteVetInstruction,
} from '../hooks/useVetInstructions';
import { useUsers } from '../hooks/useRequests';
import { format, parseISO } from 'date-fns';

const VetInstructionsPage: React.FC = () => {
  const [viewDialog, setViewDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [formData, setFormData] = useState({
    title: '',
    instruction: '',
    details: '',
    due_at: '',
    assigned_to: '',
  });

  const [completionNote, setCompletionNote] = useState('');

  const {
    data: instructionsData,
    error,
    isLoading,
    refetch,
    isFetching: isInstructionsFetching,
  } = useVetInstructions();
  const { data: users, isFetching: isUsersFetching } = useUsers();
  const createMutation = useCreateVetInstruction();
  const completeMutation = useCompleteVetInstruction();

  const filteredInstructions = React.useMemo(() => {
    if (!instructionsData?.data) return [];
    if (statusFilter === 'all') return instructionsData.data;
    return instructionsData.data.filter((inst: any) => inst.status === statusFilter);
  }, [instructionsData, statusFilter]);

  const stats = React.useMemo(() => {
    const data = instructionsData?.data || [];
    return {
      total: data.length,
      pending: data.filter((i: any) => i.status === 'pending').length,
      completed: data.filter((i: any) => i.status === 'completed').length,
      overdue: data.filter((i: any) => i.is_overdue).length,
    };
  }, [instructionsData]);

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 3,
            }}
          >
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'transparent !important',
                border: 'none',
                color: '#ffffff',
                padding: 0,
                '& .MuiAlert-icon': { color: '#f44336' },
                '& .MuiAlert-message': { color: '#ffffff', padding: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff !important' }}>
                Failed to Load Vet Instructions
              </Typography>
              <Typography
                variant="body2"
                paragraph
                sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}
              >
                {error instanceof Error
                  ? error.message
                  : 'An error occurred while fetching vet instructions.'}
              </Typography>
            </Alert>
          </Paper>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const handleOpenAddDialog = () => {
    setFormData({ title: '', instruction: '', details: '', due_at: '', assigned_to: '' });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setFormData({ title: '', instruction: '', details: '', due_at: '', assigned_to: '' });
  };

  const handleOpenViewDialog = (instruction: any) => {
    setSelectedInstruction(instruction);
    setViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setSelectedInstruction(null);
  };

  const handleOpenCompleteDialog = (instruction: any) => {
    setSelectedInstruction(instruction);
    setCompletionNote('');
    setCompleteDialog(true);
  };

  const handleCloseCompleteDialog = () => {
    setCompleteDialog(false);
    setSelectedInstruction(null);
    setCompletionNote('');
  };

  const handleSubmit = async () => {
    try {
      await createMutation.mutateAsync({
        title: formData.title,
        instruction: formData.instruction,
        details: formData.details || undefined,
        due_at: formData.due_at,
        assigned_to: Number(formData.assigned_to),
      });
      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to create instruction:', error);
    }
  };

  const handleComplete = async () => {
    if (!selectedInstruction) return;
    try {
      await completeMutation.mutateAsync({
        id: selectedInstruction.id,
        data: { completion_note: completionNote || undefined },
      });
      handleCloseCompleteDialog();
    } catch (error) {
      console.error('Failed to complete instruction:', error);
    }
  };

  const getStatusChip = (status: string, isOverdue: boolean) => {
    if (isOverdue && status === 'pending') {
      return (
        <Chip
          label="Overdue"
          size="small"
          sx={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 600 }}
        />
      );
    }
    const statusConfig = {
      pending: { label: 'Pending', color: '#ff9800' },
      completed: { label: 'Completed', color: '#2e7d32' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      color: '#757575',
    };
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.color, color: 'white', fontWeight: 600 }}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 200 },
    {
      field: 'instruction',
      headerName: 'Instruction',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.5, py: 1 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'assigned_to_user',
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => params.row.assigned_to?.name || '-',
    },
    {
      field: 'due_at',
      headerName: 'Due Date',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? format(parseISO(params.value), 'MMM dd, yyyy') : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) =>
        getStatusChip(params.row.status, params.row.is_overdue),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => handleOpenViewDialog(params.row)}
            sx={{ color: '#834105', '&:hover': { backgroundColor: 'rgba(131, 65, 5, 0.08)' } }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          {params.row.status === 'pending' && (
            <IconButton
              size="small"
              onClick={() => handleOpenCompleteDialog(params.row)}
              sx={{ color: '#2e7d32', '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
            >
              <CompleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {(isInstructionsFetching || isUsersFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      <Fab
        color="primary"
        onClick={handleOpenAddDialog}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          color: '#ffffff',
          boxShadow: '0px 6px 20px rgba(255, 152, 0, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
            boxShadow: '0px 8px 24px rgba(255, 152, 0, 0.5)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Page header */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', width: 52, height: 52 }}>
          <AssignmentTurnedInIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Vet Instructions
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Create, assign and track veterinary tasks and instructions
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(255,152,0,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(255,152,0,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,152,0,0.12)',
                  color: '#ff9800',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <AssignmentIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  TOTAL INSTRUCTIONS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#ff9800', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.total}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  all time
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(255,167,38,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(255,167,38,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,167,38,0.12)',
                  color: '#ffa726',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <PendingActionsIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  PENDING
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#ffa726', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.pending}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  awaiting action
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(102,187,106,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(102,187,106,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(102,187,106,0.12)',
                  color: '#66bb6a',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <AssignmentTurnedInIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  COMPLETED
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#66bb6a', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.completed}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  resolved
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(244,67,54,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(244,67,54,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(244,67,54,0.12)',
                  color: '#f44336',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <PendingActionsIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  OVERDUE
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#f44336', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.overdue}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  past due date
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
          border: '1px solid rgba(131, 65, 5, 0.3)',
          borderRadius: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body1" fontWeight={600} sx={{ color: '#ffffff' }}>
            Filter by Status:
          </Typography>
          <Box display="flex" gap={1}>
            {['all', 'pending', 'completed'].map((status) => (
              <Chip
                key={status}
                label={status.charAt(0).toUpperCase() + status.slice(1)}
                onClick={() => setStatusFilter(status)}
                sx={{
                  backgroundColor: statusFilter === status ? '#834105' : 'rgba(131, 65, 5, 0.2)',
                  color: statusFilter === status ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  border:
                    statusFilter === status
                      ? '1px solid #834105'
                      : '1px solid rgba(131, 65, 5, 0.3)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: statusFilter === status ? '#5a2d03' : 'rgba(131, 65, 5, 0.3)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      <Paper
        sx={{
          height: 600,
          border: '1px solid rgba(131, 65, 5, 0.3)',
          borderRadius: 3,
        }}
      >
        <DataGrid
          rows={filteredInstructions}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          getRowHeight={() => 'auto'}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: 'rgba(131, 65, 5, 0.2)',
              color: '#ffffff',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(131, 65, 5, 0.15)',
              borderBottom: '2px solid rgba(131, 65, 5, 0.3)',
              fontSize: '0.95rem',
              fontWeight: 700,
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#ffffff',
              },
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(131, 65, 5, 0.08)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid rgba(131, 65, 5, 0.3)',
              backgroundColor: 'rgba(131, 65, 5, 0.05)',
            },
          }}
        />
      </Paper>

      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(131, 65, 5, 0.3)',
            color: '#834105',
            fontWeight: 700,
          }}
        >
          Create New Vet Instruction
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Instruction"
                value={formData.instruction}
                onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Details (Optional)"
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.due_at}
                onChange={(e) => setFormData({ ...formData, due_at: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Assign To"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                required
              >
                {users?.map((user: any) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid rgba(131, 65, 5, 0.3)',
            backgroundColor: 'rgba(131, 65, 5, 0.05)',
          }}
        >
          <Button onClick={handleCloseAddDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !formData.title ||
              !formData.instruction ||
              !formData.due_at ||
              !formData.assigned_to ||
              createMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #834105 0%, #9e4f06 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #5d2e02 0%, #834105 100%)' },
              '&:disabled': { background: 'rgba(131, 65, 5, 0.3)' },
            }}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Instruction'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(131, 65, 5, 0.3)',
            color: '#834105',
            fontWeight: 700,
          }}
        >
          Instruction Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedInstruction && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Title
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {selectedInstruction.title}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Instruction
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedInstruction.instruction}
                </Typography>
              </Grid>
              {selectedInstruction.details && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Details
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedInstruction.details}
                  </Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Assigned To
                </Typography>
                <Typography variant="body1">
                  {selectedInstruction.assigned_to?.name || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Assigned By
                </Typography>
                <Typography variant="body1">
                  {selectedInstruction.assigned_by?.name || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Due Date
                </Typography>
                <Typography variant="body1">
                  {selectedInstruction.due_at
                    ? format(parseISO(selectedInstruction.due_at), 'PPP')
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                {getStatusChip(selectedInstruction.status, selectedInstruction.is_overdue)}
              </Grid>
              {selectedInstruction.status === 'completed' && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Completed At
                    </Typography>
                    <Typography variant="body1">
                      {selectedInstruction.completed_at
                        ? format(parseISO(selectedInstruction.completed_at), 'PPP p')
                        : '-'}
                    </Typography>
                  </Grid>
                  {selectedInstruction.completion_note && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Completion Note
                      </Typography>
                      <Typography variant="body1">{selectedInstruction.completion_note}</Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid rgba(131, 65, 5, 0.3)',
            backgroundColor: 'rgba(131, 65, 5, 0.05)',
          }}
        >
          <Button onClick={handleCloseViewDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completeDialog}
        onClose={handleCloseCompleteDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { border: '1px solid rgba(102,187,106,0.35)', borderRadius: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
            color: '#66bb6a',
            fontWeight: 700,
          }}
        >
          Complete Instruction
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedInstruction && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Are you sure you want to mark this instruction as completed?
              </Typography>
              <Typography variant="h6" sx={{ my: 2, color: '#66bb6a' }}>
                {selectedInstruction.title}
              </Typography>
              <TextField
                fullWidth
                label="Completion Note (Optional)"
                value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any notes about the completion..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid rgba(76, 175, 80, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Button onClick={handleCloseCompleteDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={completeMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)' },
              '&:disabled': { background: 'rgba(76, 175, 80, 0.3)' },
            }}
          >
            {completeMutation.isPending ? 'Completing...' : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VetInstructionsPage;
